import { action, makeObservable, observable, runInAction, toJS } from 'mobx';
import { buildWhereFilter, destroy, get, getCount, patch, post } from 'stores/_services/dataService';
import { getConditions, getListConditions } from 'stores/_services/filterService';
import { SortDirection } from 'constants/SortDirection';
import { IDataFetchOptions } from '../../types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const SEARCH_FIELDS = ['name', 'hostname', 'ip'];

export interface ILocation {
    id: string;
    name?: string;
    description?: string;
    organizationId?: string;
    agentCount?: number;
    alertCount?: number;
    organization?: {
        id: string;
        name: string;
    };
    displayName?: string;
    deleted: boolean;
    totalAddressCount: number;
    accessibleAddressCount: number;
    reachableAddressCount: number;
    activeAgentCount: number;
    lastScannedOn: Date | null;
    boxes: [];
    boxCount: number;
    data?: {
        monitored?: boolean;
        isDefault?: boolean;
        rmmUid?: string;
        vsaId?: string;
    };
}

export default class LocationStore {
    location: ILocation = {
        id: '',
        name: '',
        description: '',
        organizationId: '',
        deleted: false,
        totalAddressCount: 0,
        accessibleAddressCount: 0,
        reachableAddressCount: 0,
        activeAgentCount: 0,
        lastScannedOn: null,
        boxes: [],
        boxCount: 0,
        data: {}
    };

    devices = [];
    deviceFilter = {};
    deviceSort = {
        by: 'name',
        dir: SortDirection.ASC
    };
    deviceCount = 0;
    policies = [];
    policySort = {
        by: 'policyType',
        dir: SortDirection.ASC
    };

    constructor(location: ILocation) {
        makeObservable(this, {
            location: observable,
            devices: observable,
            deviceSort: observable,
            deviceFilter: observable,
            deviceCount: observable,
            policies: observable,
            policySort: observable,
            update: action,
            clearDetails: action,
            getLocationDevices: action,
            editLocation: action,
            deleteLocation: action
        });

        this.update(location);
    }

    update(location: ILocation): void {
        this.location = {
            ...this.location,
            ...location,
            displayName: `${location.name}${location.deleted ? ' (deleted)' : ''}`
        };
    }

    clearDetails(): void {
        this.policies = [];
        this.devices = [];
        this.deviceFilter = {};
        this.deviceSort = {
            by: 'name',
            dir: SortDirection.ASC
        };
    }

    async getLocationDeviceCount(): Promise<void> {
        const params = {
            where: { locationId: this.location.id }
        };
        const count = await getCount('agentDetails', params);
        runInAction(() => {
            this.deviceCount = count;
        });
    }

    async getLocationDevices(args: IDataFetchOptions = {}, sortBy?: string, sortDirection?: SortDirection): Promise<void> {
        const filter = args.filter ?? this.deviceFilter;
        const conditions = getConditions({
            location: {
                id: this.location.id
            },
            ...filter
        });

        const { data } = await get('agentDetails', {
            filter: {
                searchFields: SEARCH_FIELDS,
                where: conditions,
                include: [
                    {
                        relation: 'location',
                        scope: {
                            fields: ['name', 'data']
                        }
                    },
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ],
                order: `${sortBy ?? this.deviceSort.by} ${sortDirection ?? this.deviceSort.dir}`
            }
        });

        runInAction(() => {
            this.devices = data;
            if (sortBy) {
                this.deviceSort.by = sortBy;
            }
            if (sortDirection) {
                this.deviceSort.dir = sortDirection;
            }
            this.deviceFilter = filter;
        });

        await this.getLocationDeviceCount();
    }

    async getLocationPolicies(args: IDataFetchOptions = {}, conditions?: object): Promise<void> {
        const options = {
            filter: {
                searchFields: ['policyName', 'policyDescription'],
                order: `${args.sortBy ?? this.policySort.by} ${args.sortDirection ?? this.policySort.dir}`,
                where: conditions
            }
        };

        const { data } = await get(`locations/${this.location.id}/policyAssignments`, options);

        runInAction(() => {
            this.policies = data;
            if (args.sortBy) {
                this.policySort.by = args.sortBy;
            }
            if (args.sortDirection) {
                this.policySort.dir = args.sortDirection;
            }
        });
    }

    async existsLocationName(name: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, organizationId: this.location.organizationId }
            }
        };
        const { data } = await get('locations', options);
        return !!data.length;
    }

    async existsDeviceName(name: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, locationId: this.location.id }
            }
        };
        const { data } = await get('agents', options);
        return !!data.length;
    }

    async existsLocationNameInOrganization(name: string, organizationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, organizationId: organizationId }
            }
        };
        const { data } = await get('locations', options);
        return !!data.length;
    }

    async existsDeviceNameInLocation(name: string, locationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, locationId: locationId }
            }
        };
        const { data } = await get('agents', options);
        return !!data.length;
    }

    async editLocation(name: string, description: string): Promise<boolean> {
        const exists = name !== this.location.name && (await this.existsLocationName(name));
        if (exists) {
            return false;
        }
        const payload = {
            ...(name !== this.location.name ? { name: name } : {}),
            description: description
        };
        await patch(`locations/${this.location.id}`, payload);
        runInAction(() => {
            this.location.name = name;
            this.location.description = description;
        });
        return true;
    }

    async deleteLocation(): Promise<void> {
        const payload = {
            idsList: [this.location.id]
        };

        await post('locations/deleteLocations', payload);
    }

    async addDevice(name: string): Promise<boolean> {
        const exists = await this.existsDeviceName(name);
        if (exists) {
            return false;
        }
        const payload = {
            name: name,
            locationId: this.location.id
        };
        await post('agents', payload);
        await this.getLocationDevices();
        return true;
    }

    async renameDevice(id: string, name: string): Promise<boolean> {
        const exists = await this.existsDeviceName(name);
        if (exists) {
            return false;
        }
        const payload = {
            name: name
        };
        await patch(`agents/${id}/rename`, payload);
        await this.getLocationDevices();
        return true;
    }

    async deleteDevice(id: string): Promise<void> {
        await destroy(`agents/${id}`);
        await this.getLocationDevices();
    }

    async deleteDevices(id?: string, deviceIds?: any): Promise<void> {
        const conditions = getListConditions(id ? [id] : deviceIds);
        const options = {
            where: conditions
        };
        await destroy('agents', options);
        await this.getLocationDevices();
    }

    async moveLocation({
                           organizationId,
                           name,
                           newOrganizationName
                       }:
                           {
                               organizationId: string,
                               name: string,
                               newOrganizationName: string
                           }): Promise<boolean> {
        const exists = await this.existsLocationNameInOrganization(name, organizationId);

        const payload = {
            organizationId,
            idsList: [this.location.id]
        };
        await post('locations/moveLocations', payload);

        runInAction(() => {
            this.location.organizationId = organizationId;
            this.location.organization = {
                id: organizationId,
                name: newOrganizationName
            };
        });
        return !exists;
    }

    async moveDevices(id: string, locationId: string, deviceIds?: any): Promise<void> {
        const conditions = getListConditions(id ? [id] : deviceIds);
        const where = buildWhereFilter({
            where: conditions
        });
        const filter = { where: where };
        await post('agents/assignTarget', { targetId: locationId, filter });
        await this.getLocationDevices();
    }

    async assignPolicy(policyId: string, deviceGroupId?: string): Promise<void> {
        const payload = {
            filter: {
                where: { id: policyId }
            },
            locationId: this.location.id,
            deviceGroupId
        };

        await post('policies/assignToLocation', payload);
        await this.getLocationPolicies();
    }

    async unassignPolicy(policyAssignmentId: string): Promise<void> {
        const payload = [policyAssignmentId];
        await post(`policies/removePolicyAssignments`, payload);
        await this.getLocationPolicies();
    }

    async enablePolicy(policyId: string, deviceGroupId: string, policyAssignmentId: string): Promise<void> {
        const payload = {
            policyId,
            deviceGroupId,
            policyAssignmentId
        };

        await post(`policies/enableForLocation/${this.location.id}`, payload);
        await this.getLocationPolicies();
    }

    async disablePolicy(policyId: string, deviceGroupId: string, policyAssignmentId: string): Promise<void> {
        const payload = {
            policyId,
            deviceGroupId,
            policyAssignmentId
        };

        await post(`policies/disableForLocation/${this.location.id}`, payload);
        await this.getLocationPolicies();
    }
}
